// A sales order part's surplus: what the "+N surplus" badge and the Allocate button may offer.
// ONE copy, used by Job Entry's Sales Order Fulfillment table (pages/JobEntryPage.jsx
// SalesOrderDashboard) and the Sales Order page's Line Items (pages/SalesOrderPage.jsx).
//
// /truth's qty_in_production counts every piece headed to a line, INCLUDING pieces another
// order's job sends here through a surplus allocation (incoming_allocations) and placements
// another order's job made into this order's packing plan with no allocation row (allocated_in_placed
// holds every placement by another order's job, so those and the ROUTED rows' pieces). POST /surplus_allocations (jobq-api create_surplus_allocations, the
// same-line path) counts only THIS order's own jobs: Σ job_parts − defects − ordered − active
// same-line allocations. So pieces that came in from another order are not this order's surplus:
// SO-1139 FAB311162 on 2026-10-21 showed "+31 surplus" + Allocate (655 in prod = 562 own + 93
// from SO-1131, 624 ordered) and the POST failed "Over-allocation: SO jobs produced 562 ...
// surplus=-62". That extra is over-coverage: SO-1131's allocation sends 31 more than this order
// needs and should be trimmed. Only the order's own over-production can be allocated on.
//
// Outgoing allocations: qty_in_production is already net of each one's ROUTED pieces
// (outgoing_allocations[].quantity), so only the unrouted remainder (intent_quantity − quantity)
// is still committed inside it. Together that subtracts at least each allocation's intent, as the
// server does.
//
// A part on 2+ lines: /truth (jobq-api 58afb13, Aug 2026: main.py "Shared-primary mirror
// assemblies", _truth_split_shared_part_lines) splits the part's figures across its lines in
// so_line_id order, so the shares SUM to the part's pool. Since jobq-api 2026-10-21 that pool is the
// part's own production + EVERY line's allocation terms, and each line says what its terms are:
// in_production_incoming (its allocations in, + on the part's first line the placements another
// order's job made with no allocation row) and in_production_outgoing (its allocations out, + on the
// first line this order's placements into another order's plan with no row). Each allocation row's
// quantity is its share across all the lines, so a job allocated to two lines is listed once. An API
// from before carries no terms: its pool held only the FIRST line's allocations (another line's never
// reached it) and a routed job on two lines was listed in full on both; termsOnLines() tells which.
//
// Routed vs unrouted (jobq-api, 2026-10-21): incoming_allocations lists exactly the rows
// qty_in_production counts (a row stays once its source Shipping task is Completed; a job with no
// Shipping task for the part, an assembly component or Fabrication-only job, is listed too) and each
// row says `routed`. A routed row's pieces are placements, so they are inside allocated_in_placed; an
// unrouted row's quantity is intent and is not. `routed` = routing could put the row's pieces into
// THIS ORDER: the source job has routed and either placed pieces here or this order has a committed
// plan with zones for the part. A job that routed only into its own order's plan while this order has
// no plan (or a Draft one), or a component this order's plan has no zones for, is unrouted here and
// counts its intent (it used to read 0, so the line looked short and was offered again: SO-1106
// PERFTABLEPART2B read -1). So a part's incoming = Σ unrouted rows +
// allocated_in_placed (100 ordered, 100 own, 50 unrouted from SO-A + 30 placed by a sibling job =
// 80; the larger of the two was 50, so Allocate offered 30 the server refused). A routed job with
// rows on two lines of the part is listed with all its placements on both, so it counts once
// (countedIncoming). An older API without `routed` (and without the Completed rows) falls back to the
// larger of the list and allocated_in_placed.
// With the terms (2026-10-21) incoming is in_production_incoming as it stands: exact. Without them
// (an older API) it errs toward offering less: allocated_in_placed also holds placements
// qty_in_production skips (a Cancelled job's, or a job whose only Shipping task is Voided), which then
// read as incoming: less to Allocate and a "+N over" badge for pieces that aren't coming.

const num = (v) => (Number.isFinite(+v) ? +v : 0);
const list = (v) => (Array.isArray(v) ? v : []);
const sumQty = (allocs) => list(allocs).reduce((s, a) => s + num(a && a.quantity), 0);
const hasNum = (v) => v != null && v !== '' && Number.isFinite(+v);

/** Every line carries its allocation terms (jobq-api since 2026-10-21): in_production_incoming /
 *  in_production_outgoing are inside the part's split figure, and allocation rows are per-line shares. */
export function termsOnLines(lines) {
  const ls = list(lines).filter(Boolean);
  return ls.length > 0 && ls.every((l) => hasNum(l.in_production_incoming) && hasNum(l.in_production_outgoing));
}

/** One part's incoming rows as qty_in_production counts them: { perLine, listed, routed, flagged }.
 *  With the terms on the lines every row is its own share: each counts once. From an older API a
 *  routed row reports its source job's placements into this order, listed in full on EVERY line the
 *  job has rows for, so a routed job counts once, on the lowest such line (the one inside the split
 *  figure when it is the part's first). `flagged` = every row says `routed`; without it (an older
 *  API still) rows count as listed. `grp` = the /truth lines of one part. */
function countedIncoming(grp) {
  if (termsOnLines(grp)) {
    // Each row's quantity is its share across the part's lines: every row counts, once.
    const perLine = {};
    let listed = 0;
    let routed = 0;
    list(grp).forEach((l) => {
      perLine[l.sales_order_line_id] = sumQty(l.incoming_allocations);
      listed += perLine[l.sales_order_line_id];
      list(l.incoming_allocations).forEach((a) => { if (a && a.routed === true) routed += num(a.quantity); });
    });
    return { perLine, listed, routed, flagged: true, exact: true };
  }
  const perLine = {};
  const raw = {};
  let flagged = true;
  let listedRaw = 0;
  let listed = 0;
  let routed = 0;
  const claimed = new Set();
  [...list(grp)].sort((a, b) => num(a.sales_order_line_id) - num(b.sales_order_line_id)).forEach((l) => {
    const here = new Set();
    let lineQty = 0;
    raw[l.sales_order_line_id] = sumQty(l.incoming_allocations);
    listedRaw += raw[l.sales_order_line_id];
    list(l.incoming_allocations).forEach((a) => {
      const q = num(a && a.quantity);
      if (a && a.routed === true) {
        const job = a.source_job_id;
        if (job != null && claimed.has(job)) return;
        if (job != null) here.add(job);
        routed += q;
      } else if (!(a && a.routed === false)) {
        flagged = false;
      }
      lineQty += q;
    });
    here.forEach((j) => claimed.add(j));
    perLine[l.sales_order_line_id] = lineQty;
    listed += lineQty;
  });
  return flagged ? { perLine, listed, routed, flagged } : { perLine: raw, listed: listedRaw, routed: 0, flagged };
}

const byPart = (lines) => {
  const groups = new Map();
  list(lines).forEach((l) => {
    if (!l || l.part_id == null || l.sales_order_line_id == null) return;
    if (!groups.has(l.part_id)) groups.set(l.part_id, []);
    groups.get(l.part_id).push(l);
  });
  return groups;
};

/** Pieces other orders send to each line, keyed by sales_order_line_id. With the terms on the lines:
 *  in_production_incoming. Without (an older API): its incoming allocations' quantity (routed reality
 *  once the source job has routed, intent before; a routed job once per part, see countedIncoming),
 *  plus, on the part's FIRST line (where qty_in_production adds them), placements another order's
 *  job made into this order's plan that no allocation row names (allocated_in_placed is per order +
 *  part, and holds the routed rows' pieces too). `lines` = flattened /truth lines. */
export function incomingByLine(lines) {
  const out = {};
  byPart(lines).forEach((grp) => {
    if (termsOnLines(grp)) {
      // The line's term as qty_in_production counts it (rows + the placements with no row).
      grp.forEach((l) => { out[l.sales_order_line_id] = Math.max(0, num(l.in_production_incoming)); });
      return;
    }
    const { perLine, listed, routed, flagged } = countedIncoming(grp);
    Object.assign(out, perLine);
    const placed = Math.max(0, ...grp.map((l) => num(l.allocated_in_placed)));
    // The placements no row names: with `routed` on every row, what the routed rows don't account
    // for; without it (an older API), what the whole list doesn't.
    const unlisted = Math.max(0, placed - (flagged ? routed : listed));
    if (unlisted > 0) {
      const first = grp.reduce((a, l) => (l.sales_order_line_id < a.sales_order_line_id ? l : a));
      out[first.sales_order_line_id] += unlisted;
    }
  });
  return out;
}

/** Unrouted remainder of a line's outgoing allocations (intent − routed): still promised to
 *  another order but not yet netted out of qty_in_production. */
export function outgoingOpen(allocs) {
  return list(allocs).reduce(
    (s, a) => s + Math.max(0, num(a && (a.intent_quantity ?? a.quantity)) - num(a && a.quantity)),
    0,
  );
}

/** Split what is headed to an order for one part into its own surplus and over-coverage.
 *  inProduction = qty_in_production as shown (includes `incoming`, net of routed outgoing);
 *  staged = Job Entry's rows being created now (own jobs once saved; 0 elsewhere);
 *  committed = own surplus already promised elsewhere (outgoingOpen + allocations pending in
 *  this Job Entry session).
 *  Returns:
 *   surplus      own over-production still on the floor (shipped pieces cannot be allocated)
 *   allocatable  surplus − committed: what Allocate may offer; never more than the server accepts
 *   incoming     the pieces other orders send (as passed in)
 *   overCoverage pieces coming from other orders beyond what this order still needs
 *   incomingNeeded the part of `incoming` this order does need */
export function splitSurplus({ ordered, shipped = 0, inProduction = 0, staged = 0, incoming = 0, committed = 0 }) {
  const need = num(ordered);
  const inc = Math.max(0, num(incoming));
  const excess = Math.max(0, num(shipped) + num(inProduction) + num(staged) - need);
  const ownFloor = Math.max(0, num(inProduction) + num(staged) - inc);
  const surplus = Math.max(0, Math.min(num(shipped) + ownFloor - need, ownFloor));
  const allocatable = Math.max(0, surplus - Math.max(0, num(committed)));
  const overCoverage = Math.min(inc, excess);
  return { excess, surplus, allocatable, incoming: inc, overCoverage, incomingNeeded: inc - overCoverage };
}

/** One part's surplus, from every /truth line carrying it (`members`): Job Entry merges them into
 *  one row, the Sales Order page shows Allocate once, on the part's last line (surplusByLastLine).
 *  The shares /truth split across the lines SUM to the pool. With the terms on the lines the pool
 *  holds every line's allocations, so they all come back out to find the order's own pieces; from an
 *  older API only the first line's are inside it. Then every line's allocations are applied once.
 *  `incoming` = incomingByLine(all lines). One line: exactly its qty_in_production. */
export function partSurplus(members, { staged = 0, pending = 0, incoming = {} } = {}) {
  const lines = list(members).filter(Boolean);
  const ordered = lines.reduce((s, l) => s + num(l.quantity_ordered), 0);
  const shipped = lines.reduce((s, l) => s + num(l.qty_shipped), 0);
  const pool = lines.reduce((s, l) => s + num(l.qty_in_production), 0);
  let incomingQty = 0;
  let routedOut = 0;
  let open = 0;
  lines.forEach((l) => {
    incomingQty += num(incoming[l.sales_order_line_id]);
    routedOut += sumQty(l.outgoing_allocations);
    open += outgoingOpen(l.outgoing_allocations);
  });
  const first = lines.length
    ? lines.reduce((a, l) => (num(l.sales_order_line_id) < num(a.sales_order_line_id) ? l : a))
    : null;
  const inside = termsOnLines(lines) ? lines : (first ? [first] : []);
  const own = inside.reduce(
    (s, l) => s - num(incoming[l.sales_order_line_id]) + sumQty(l.outgoing_allocations), pool,
  );
  const inProduction = Math.max(0, own + incomingQty - routedOut);
  const split = splitSurplus({
    ordered, shipped, inProduction, staged, incoming: incomingQty, committed: open + num(pending),
  });
  return {
    ordered, shipped, inProduction, incoming: incomingQty, committedOut: open,
    sources: incomingSources(lines, incomingQty), ...split,
  };
}

/** The Sales Order page's per-part surplus, keyed by the line that shows it: the part's last
 *  plain (non purchased-item) line in so_line_id order, where /truth parks a part's excess. */
export function surplusByLastLine(lines) {
  const incoming = incomingByLine(lines);
  const groups = new Map();
  list(lines).forEach((l) => {
    if (!l || l.part_id == null || l.sales_order_line_id == null) return;
    if (!groups.has(l.part_id)) groups.set(l.part_id, []);
    groups.get(l.part_id).push(l);
  });
  const out = {};
  groups.forEach((grp) => {
    const shown = grp.filter((l) => !l.consumable_id);
    if (!shown.length) return;
    const last = shown.reduce((a, l) => (l.sales_order_line_id > a.sales_order_line_id ? l : a));
    out[last.sales_order_line_id] = partSurplus(grp, { incoming });
  });
  return out;
}

const PLACED = "another order's job";

/** Who sends this part here, e.g. ["SO-1131"]; "another order's job" stands for pieces that came
 *  by packing-plan placement with no allocation row (incoming beyond the listed allocations). */
export function incomingSources(members, incomingTotal = 0) {
  const names = [];
  let listed = 0;
  byPart(members).forEach((grp) => { listed += countedIncoming(grp).listed; });
  list(members).forEach((l) => list(l && l.incoming_allocations).forEach((a) => {
    if (!(num(a && a.quantity) > 0)) return;
    const n = a.source_so_order_number || (a.source_job_num ? `Job ${a.source_job_num}` : null);
    if (n && !names.includes(n)) names.push(n);
  }));
  if (num(incomingTotal) > listed) names.push(PLACED);
  return names;
}

/** The over-coverage badge: "+31 over" with "SO-1131 sends 93, only 62 needed", and a tooltip
 *  saying those pieces are the sender's, changed on the sender's allocation. null when none. */
export function overCoverageText({ overCoverage, incoming, incomingNeeded }, sources) {
  if (!(overCoverage > 0)) return null;
  const names = list(sources).length ? list(sources) : [PLACED];
  const from = names.join(', ');
  const needed = incomingNeeded > 0 ? `only ${incomingNeeded} needed` : 'none needed';
  const allocated = names.filter((n) => n !== PLACED);
  let fix = '';
  if (allocated.length === 1) {
    fix = ` To send them to another order, trim ${allocated[0]}'s allocation to this order`
      + ` to ${incomingNeeded} and allocate them from ${allocated[0]}.`;
  } else if (allocated.length > 1) {
    fix = ` To send them to another order, trim the allocations to this order to ${incomingNeeded}`
      + ' in total and allocate them from the order they come from.';
  }
  return {
    label: `+${overCoverage} over`,
    detail: `${from} send${names.length > 1 ? '' : 's'} ${incoming}, ${needed}`,
    title: `${overCoverage} more than this order needs are coming from ${from} (${incoming} sent, `
      + `${incomingNeeded} needed). They are not this order's own production, so they cannot be `
      + `allocated from here.${fix}`,
  };
}

/** Each part's In Prod on the order, keyed by part_id (a Map): what Job Entry's Sales Order
 *  Fulfillment row shows for it (partSurplus over all the part's lines: /truth's split shares
 *  summed, every line's allocations applied once; with the terms on the lines that is the plain sum
 *  of the shares). Job Entry's bulk grid reads it for its per-row hint. `lines` = flattened /truth
 *  lines. */
export function inProductionByPart(lines) {
  const incoming = incomingByLine(lines);
  const groups = new Map();
  list(lines).forEach((l) => {
    if (!l || l.part_id == null || l.sales_order_line_id == null) return;
    if (!groups.has(l.part_id)) groups.set(l.part_id, []);
    groups.get(l.part_id).push(l);
  });
  const out = new Map();
  groups.forEach((grp, partId) => out.set(partId, partSurplus(grp, { incoming }).inProduction));
  return out;
}
